import { useState, useMemo, useEffect } from 'react';

/**
 * Client-side sort + paginate for tabular data.
 *
 * Usage:
 *   const { pageItems, page, pageSize, totalPages, totalItems,
 *           sortKey, sortDir, toggleSort, setPage, setPageSize } =
 *     usePaginatedSort(filteredItems, { defaultSortKey: 'name' });
 *
 * Comparator handles strings (localeCompare), numbers, booleans, dates, and
 * null/undefined (always last). Pass a `customCompare(a, b, key, dir)` to
 * override for a specific column (e.g. status pill ordering).
 *
 * Page auto-clamps when the filtered set shrinks (e.g. typing in a search box).
 */
export function usePaginatedSort(items, {
  defaultSortKey = null,
  defaultPageSize = 25,
  customCompare = null,
} = {}) {
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const sorted = useMemo(() => {
    if (!sortKey) return items;
    const copy = [...items];
    copy.sort((a, b) => {
      if (customCompare) {
        const r = customCompare(a, b, sortKey, sortDir);
        if (r !== undefined) return r;
      }
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      // null/undefined always last (regardless of direction)
      const aNull = av === null || av === undefined || av === '';
      const bNull = bv === null || bv === undefined || bv === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else if (typeof av === 'boolean' && typeof bv === 'boolean') cmp = av === bv ? 0 : av ? -1 : 1;
      else if (av instanceof Date && bv instanceof Date) cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), 'es', { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sortKey, sortDir, customCompare]);

  const totalItems = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  const pageItems = sorted.slice(start, start + pageSize);

  // Clamp page when totalPages shrinks beneath current page (e.g. after a search)
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [safePage, page]);

  function toggleSort(key) {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  return {
    pageItems,
    page: safePage,
    pageSize,
    totalPages,
    totalItems,
    sortKey,
    sortDir,
    toggleSort,
    setPage,
    setPageSize,
  };
}
